import { useCallback, useRef, useState } from 'react'
import { CHAT_STREAM_INTERRUPTED, isChatThreadDeletedError, streamChatApi } from './chatApi'
import type { ChatPageContext } from './pageContext'

export type StreamSendResult = 'completed' | 'aborted' | 'error' | 'thread_deleted'

export function useChatStream() {
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const cancel = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const send = useCallback(
    async (args: {
      threadId?: string
      message: string
      kind: 'personal' | 'shared'
      pageContext: ChatPageContext
      retry?: boolean
      onUserPersisted?: () => void
      onThread?: (threadId: string) => void
      onAssistantDelta?: (text: string) => void
      onTool?: (name: string) => void
      onComplete?: (toolsUsed: string[]) => void
      onAbort?: () => void
    }): Promise<StreamSendResult> => {
      abortRef.current?.abort()
      setError(null)
      setStreaming(true)
      const controller = new AbortController()
      abortRef.current = controller
      let receivedDone = false
      let streamFailed: string | null = null
      try {
        await streamChatApi(
          {
            threadId: args.threadId,
            message: args.message,
            kind: args.kind,
            pageContext: args.pageContext,
            retry: args.retry,
          },
          {
            onUserPersisted: () => args.onUserPersisted?.(),
            onThread: (threadId) => args.onThread?.(threadId),
            onDelta: (text) => args.onAssistantDelta?.(text),
            onTool: (name) => args.onTool?.(name),
            onDone: (toolsUsed) => {
              receivedDone = true
              args.onComplete?.(toolsUsed)
            },
            onError: (message) => {
              streamFailed = message
              setError(message)
            },
          },
          controller.signal
        )
        if (controller.signal.aborted) {
          args.onAbort?.()
          return 'aborted'
        }
        if (streamFailed) return 'error'
        if (!receivedDone) {
          setError(CHAT_STREAM_INTERRUPTED)
          return 'error'
        }
        return 'completed'
      } catch (err) {
        if ((err as Error).name === 'AbortError' || controller.signal.aborted) {
          args.onAbort?.()
          return 'aborted'
        }
        if (isChatThreadDeletedError(err)) {
          return 'thread_deleted'
        }
        if (!streamFailed) {
          setError(err instanceof Error ? err.message : String(err))
        }
        return 'error'
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null
        }
        setStreaming(false)
      }
    },
    []
  )

  return { send, cancel, streaming, error, setError }
}
